<template>
  <div class="border rounded-lg py-4">
    <h3 class="pl-3">Alertas de Assinaturas</h3>
    <div class="d-flex flex-wrap px-3 my-3">
      <font-awesome-icon
        icon="filter"
        class="py-1 pr-3 align-self-center text-muted"
        size="sm"
      />
      <b-button
        v-for="filter in alertFilters"
        :key="filter.value"
        :class="{ active: type === filter.value }"
        class="mr-2 mb-0"
        size="sm"
        variant="outline-exitos"
        @click="filterAlerts(filter.value)"
      >
        {{ filter.label }}
      </b-button>
      <b-button
        v-if="hasCountAlerts"
        size="sm"
        variant="outline-exitos"
        class="ml-auto mb-0 border-0"
        @click="markAllRead"
      >
        <font-awesome-icon icon="circle-check" />
        Marcar todos como lido
      </b-button>
    </div>
    <b-table
      hover
      :items="alerts"
      :fields="fields"
      show-empty
      :busy="isBusy"
      @sort-changed="sortingChanged"
    >
      <template #cell(created_at)="{ value, item }">
        <div class="d-inline-flex align-items-center">
          <b-button
            @click="readAlert(item, false)"
            :disabled="!!item?.read_at"
            variant="outline-exitos"
            title="Marcar como lido"
            v-b-tooltip.hover
            class="border-0 rounded-circle circle-icon-button"
          >
            <font-awesome-icon icon="check" />
          </b-button>
          <span
            class="text-capitalize-first d-inline-block cursor-default"
            v-b-tooltip.hover
            :title="value | humanize_datetime"
          >
            {{ value | format_datetime }}
          </span>
          <span v-if="!item?.read_at" class="pl-2">
            <font-awesome-icon icon="circle" size="xs" class="text-danger" />
          </span>
        </div>
      </template>
      <template #cell(message)="{ value, item }">
        <div>
          <div v-text="value" class="mb-0 text-body text-wrap" />
          <span v-text="item?.motivo" class="text-gray" />
        </div>
      </template>
      <template #cell(type)="{ value }">
        <span
          v-if="value"
          class="py-1 px-2 font-weight-600 rounded-pill custom-badge"
          :class="value?.color"
          v-text="value?.label"
        />
      </template>
      <template #cell(action)="{ item }">
        <b-button
          v-if="item?.pedido_id"
          size="sm"
          variant="outline-exitos"
          class="text-nowrap"
          @click="readAlert(item)"
        >
          VER PEDIDO
        </b-button>
      </template>
      <template #empty="{}">
        <div
          class="w-100 text-center d-inline-flex align-items-center align-content-center flex-column my-4 py-3"
        >
          <div class="p-3 bg-empty mb-2">
            <font-awesome-icon icon="inbox" size="2xl" class="text-exitos" />
          </div>
          <h4 class="mb-1">Nada por aqui</h4>
          <p class="text-muted">Não há alertas a serem exibidos por enquanto</p>
        </div>
      </template>

      <template #table-busy>
        <div class="text-center my-4">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>
    </b-table>
    <div class="px-3">
      <pagination-nav :pagination="pagination" @input="loadData()" />
    </div>
  </div>
</template>
<script>
import { tableMixins } from "@/components/Table/mixins";
import {
  alertFilterKeys,
  enumAlerts,
  getPropByKey,
  mapAlerts,
} from "@/modules/config/helpers/AlertMap";
import { isEqual } from "lodash";
import { mapActions, mapState } from "vuex";

export default {
  mixins: [tableMixins()],
  emits: ["read-alert"],
  data: () => ({
    type: enumAlerts.ALL,
  }),
  computed: {
    ...mapState("config", ["alerts", "count_alerts", "pagination"]),
    alertFilters() {
      return alertFilterKeys.map((value) => ({
        value,
        label: getPropByKey(value, "label"),
      }));
    },
    hasCountAlerts() {
      return this.count_alerts > 0;
    },
    fields() {
      return [
        {
          key: "created_at",
          label: "",
          class: "col-2 align-middle",
          sortable: true,
        },
        {
          key: "message",
          label: "Descrição",
          class: "align-middle",
        },
        {
          key: "type",
          sortable: true,
          label: "Tag",
          class: "align-middle text-center",
          formatter: (value) => {
            if (!Reflect.has(mapAlerts, value)) return value;
            return Reflect.get(mapAlerts, value);
          },
        },
        {
          key: "action",
          label: "",
          class: "align-middle",
        },
      ];
    },
  },
  methods: {
    ...mapActions("config", [
      "ActionGetAll",
      "ActionSetPagination",
      "ActionSetRead",
    ]),
    getPropByKey,
    filterAlerts(type) {
      this.type = type;
      this.pagination.current_page = 1;
      this.loadData();
    },
    async loadData() {
      try {
        this.isBusy = true;
        const payload = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          orderBy: this.sortBy,
          sortBy: this.sortDesc ? "desc" : "asc",
          q: this.termo_pesquisa,
          type: this.type,
        };

        if (!isEqual(this._last_request, payload)) {
          this._last_request = payload;
          await this.ActionGetAll(payload);
        }
      } catch (error) {
        this.$notifications.addNotification({
          message: "Erro ao carregar dados da listagem",
          icon: "exclamation-triangle",
          type: "danger",
        });
      } finally {
        this.isBusy = false;
      }
    },
    markAllRead() {
      const payload = {
        ler_tudo: true,
      };

      this.markRead(payload);
    },
    async readAlert($event, redirect = true) {
      const ids = [$event?.id];
      const existsIds = ids.every((id) => !isNaN(Number(id)));

      if (!existsIds) return;

      const payload = $event?.read_at ? null : { ids };

      await this.markRead(payload, $event?.pedido_id, redirect);
    },
    async markRead(payload, idToRedirect = null, redirect = false) {
      try {
        const result = payload ? await this.ActionSetRead(payload) : null;

        if (redirect) return;

        if (result?.message) {
          this.$notifications.addNotification({
            message: result.message,
            type: "success",
          });
        }
      } catch (error) {
        this.$notifications.addNotification({
          message: "Erro ao tentar alterar status do alerta",
          type: "danger",
        });
      } finally {
        if (!redirect || !idToRedirect) return;

        await this.$router.push({
          name: "pedidos_show",
          params: { id: idToRedirect },
        });
      }
    },
  },
};
</script>
<style scoped lang="scss">
.bg-empty {
  background-color: rgba(34, 83, 115, 0.05);
  border-radius: 100vw;
}

.custom-badge {
  min-width: 133px;
  width: max-content;
  display: block;
  margin: 0 auto;
}

.circle-icon-button {
  aspect-ratio: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}
</style>
